import {
    AppBar,
    Button,
    Collapse,
    createStyles,
    Divider,
    IconButton,
    Link,
    LinkProps as MuiLinkProps,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem,
    Theme,
    Toolbar,
    withStyles,
    WithStyles,
    ButtonProps
} from "@material-ui/core";
import { ExpandLess, ExpandMore, Menu as MenuIcon } from "@material-ui/icons";
import React from "react";
import { connect, useSelector } from "react-redux";
import { LinkProps, Link as ReactLink } from "react-router-dom";
import { bindActionCreators } from "redux";
import Constants from "../../models/utility/Constants";
import SecurityProvider, {
    PermissionType
} from "../../providers/SecurityProvider";
import NavbarImage from "./../../assets/clhbid_logo_white.png";
import { IAppDispatch, IAppState } from "./../../store";
import * as UserStore from "./../../store/UserStore";
import * as AppConfigStore from "../../store/AppConfigStore";

type NavMenuProps = UserStore.IUserStoreState &
    AppConfigStore.IAppConfigStoreState &
    typeof UserStore.actionCreators &
    WithStyles<typeof styles>;

interface INavMenuState {
    adminMenuAnchor: HTMLElement | null;
    adminMobileMenuOpen: boolean;
    mobileMenuAnchor: HTMLElement | null;
}

interface INavItem {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element: ((props: any) => JSX.Element | null) | null;
    onClickMenu?: () => void;
    permission?: PermissionType;
    text: string;
}

const styles = (theme: Theme) =>
    createStyles({
        linkDivider: {
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: "inline",
            height: 36,
            marginLeft: 7,
            marginRight: 7,
            paddingBottom: 6,
            paddingTop: 6
        },
        mobileMenuItem: {
            [theme.breakpoints.down("xs")]: {
                "& span": { fontSize: 14 },
                fontSize: 14
            },
            minWidth: 200,
            textTransform: "none"
        },
        mobileMenuItemNested: { paddingLeft: theme.spacing(2) },
        navbar: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: theme.bodyContent.mobilePadding,
                paddingRight: theme.bodyContent.mobilePadding
            },
            height: theme.navbar.height,
            left: 0,
            lineHeight: `${theme.navbar.height}px`,
            margin: "0 auto",
            maxWidth: theme.layout.maxWidth,
            paddingLeft: theme.bodyContent.desktopPadding,
            paddingRight: theme.bodyContent.desktopPadding,
            width: "100%",
            backgroundColor: "#424242"
        },
        navbarContent: {
            alignItems: "center",
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            margin: "0 auto",
            maxWidth: theme.bodyContent.maxWidth,
            width: "100%",
            backgroundColor: "#424242"
        },
        navbarImage: {
            display: "block",
            height: 35,
            marginRight: theme.spacing(3),
            width: "auto"
        },
        navbarLinksDesktop: {
            [theme.breakpoints.down("sm")]: { display: "none" }
        },
        navbarLinksMobile: {
            [theme.breakpoints.down("sm")]: { display: "block" },
            display: "none"
        },
        navbarMobileButton: { marginRight: -12 }
    });

class NavMenu extends React.PureComponent<NavMenuProps, INavMenuState> {
    constructor(props: NavMenuProps) {
        super(props);
        this.state = {
            adminMenuAnchor: null,
            adminMobileMenuOpen: false,
            mobileMenuAnchor: null
        };
    }

    public render() {
        const classes = this.props.classes;

        return (
            <AppBar
                className={classes.navbar}
                position="fixed"
                color="default"
                data-testid="NavMenu"
            >
                <Toolbar
                    className={classes.navbarContent}
                    disableGutters={true}
                >
                    <Link href={Constants.Routes.Home()}>
                        <img
                            className={classes.navbarImage}
                            src={NavbarImage}
                            width="338"
                            height="60"
                            alt="CLHBid.com"
                        />
                    </Link>
                    <div className={classes.navbarLinksDesktop}>
                        {this.renderNavLinks(false)}
                    </div>
                    <div className={classes.navbarLinksMobile}>
                        <IconButton
                            className={classes.navbarMobileButton}
                            color="inherit"
                            aria-label="Menu"
                            onClick={this.onOpenMobileMenu}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Mobile Menu */}
                        <Menu
                            anchorEl={this.state.mobileMenuAnchor}
                            open={Boolean(this.state.mobileMenuAnchor)}
                            onClose={this.onCloseMobileMenu}
                        >
                            {this.renderNavLinks(true)}
                        </Menu>

                        {/* Admin Menu */}
                        <Menu
                            anchorEl={this.state.adminMenuAnchor}
                            open={Boolean(this.state.adminMenuAnchor)}
                            onClose={this.onCloseAdminMenu}
                        >
                            {this.adminLinks.map((e, idx) =>
                                this.renderNavLink(idx, e, true)
                            )}
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    private onOpenMobileMenu = (e: React.MouseEvent) =>
        this.setState({ mobileMenuAnchor: e.currentTarget as HTMLElement });
    private onCloseMobileMenu = () =>
        this.setState({ adminMobileMenuOpen: false, mobileMenuAnchor: null });

    private onOpenAdminMenu = (e: React.MouseEvent) =>
        this.setState({ adminMenuAnchor: e.currentTarget as HTMLElement });
    private onCloseAdminMenu = () => this.setState({ adminMenuAnchor: null });

    private toggleAdminMobileMenu = () =>
        this.setState({ adminMobileMenuOpen: !this.state.adminMobileMenuOpen });

    private renderNavLinks = (isMenu: boolean) => {
        if (this.props.user) {
            return this.loggedInLinks(this.props.user.name, isMenu).map(
                (e, idx) => this.renderNavLink(idx, e, isMenu)
            );
        }

        return this.loggedOutLinks.map((e, idx) =>
            this.renderNavLink(idx, e, isMenu)
        );
    };

    private renderNavLink = (idx: number, item: INavItem, menu: boolean) => {
        const classes = this.props.classes;

        // check permission; do not render if they aren't allowed to see the link(s)
        if (item.permission && !item.permission(this.props.user)) {
            return null;
        }

        // menu links
        if (menu) {
            if (item.element === null) {
                return <Divider key={idx} />;
            }

            const onClick = item.onClickMenu
                ? item.onClickMenu
                : this.onCloseMobileMenu;

            return (
                <MenuItem
                    key={idx}
                    className={classes.mobileMenuItem}
                    component={item.element}
                    onClick={onClick}
                >
                    {item.text}
                </MenuItem>
            );
        }

        // desktop links
        if (item.element === null) {
            return <div key={idx} className={classes.linkDivider} />;
        }

        return (
            <Button key={idx} component={item.element} color="inherit">
                {item.text}
            </Button>
        );
    };

    private adminAuctionListLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.AdminAuctionList()} {...props} />
    );
    private adminUserListLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.AdminUserList()} {...props} />
    );
    private adminLinkDesktop = (props: Partial<ButtonProps>) => (
        <Button {...props} onClick={this.onOpenAdminMenu} />
    );
    private adminLinkMobile = () => {
        const classes = this.props.classes;
        return (
            <>
                <ListItem
                    className={classes.mobileMenuItem}
                    onClick={this.toggleAdminMobileMenu}
                >
                    <ListItemText>Admin</ListItemText>
                    {this.state.adminMobileMenuOpen ? (
                        <ExpandLess />
                    ) : (
                        <ExpandMore />
                    )}
                </ListItem>
                <Collapse in={this.state.adminMobileMenuOpen}>
                    <List
                        className={classes.mobileMenuItemNested}
                        disablePadding={true}
                    >
                        {this.adminLinks.map((e, idx) =>
                            this.renderNavLink(idx, e, true)
                        )}
                    </List>
                </Collapse>
            </>
        );
    };

    private auctionsLink = (props: Partial<MuiLinkProps>) => {
        const baseUrl = useSelector<IAppState, string>(
            (state) => state.appConfigStoreState.salesPageUrl
        );

        return (
            <Link
                href={Constants.Routes.AuctionList({ baseUrl }).href}
                color="inherit"
                underline="none"
                {...props}
            />
        );
    };
    private homeLink = (props: Partial<MuiLinkProps>) => (
        <Link
            href={Constants.Routes.Home()}
            color="inherit"
            underline="none"
            {...props}
        />
    );
    private logoutLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.Logout()} {...props} />
    );
    private loginLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.Login()} {...props} />
    );
    private profileLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.Profile()} {...props} />
    );
    private registerLink = (props: Partial<LinkProps>) => (
        <ReactLink to={Constants.Routes.Register()} {...props} />
    );

    // tslint:disable-next-line:member-ordering
    private adminLinks: INavItem[] = [
        {
            element: this.adminAuctionListLink,
            onClickMenu: () => {
                this.onCloseAdminMenu();
                this.onCloseMobileMenu();
            },
            permission: SecurityProvider.CanManageAuctionsAndParcels,
            text: "Manage Auctions"
        },
        {
            element: this.adminUserListLink,
            onClickMenu: () => {
                this.onCloseAdminMenu();
                this.onCloseMobileMenu();
            },
            permission: SecurityProvider.CanManageUsers,
            text: "Manage Users"
        }
    ];

    private loggedInLinks = (userName: string, mobile: boolean): INavItem[] => [
        {
            element: this.homeLink,
            permission: SecurityProvider.AnonymousAccess,
            text: "Home"
        },
        {
            element: this.auctionsLink,
            permission: SecurityProvider.AnonymousAccess,
            text: "Auctions"
        },
        {
            element: mobile ? this.adminLinkMobile : this.adminLinkDesktop,
            permission: SecurityProvider.CanSeeAdminLink,
            text: "Admin"
        },
        // divider
        {
            element: null,
            permission: SecurityProvider.AnonymousAccess,
            text: ""
        },
        {
            element: this.profileLink,
            permission: SecurityProvider.AnonymousAccess,
            text: userName
        },
        {
            element: this.logoutLink,
            permission: SecurityProvider.AnonymousAccess,
            text: "Logout"
        }
    ];

    // tslint:disable-next-line:member-ordering
    private loggedOutLinks: INavItem[] = [
        {
            element: this.homeLink,
            text: "Home"
        },
        {
            element: this.auctionsLink,
            text: "Auctions"
        },
        // divider
        {
            element: null,
            text: ""
        },
        {
            element: this.loginLink,
            text: "Login"
        },
        {
            element: this.registerLink,
            text: "Register"
        }
    ];
}

const reduxState = (state: IAppState) => ({
    ...state.userStoreState,
    ...state.appConfigStoreState
});
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(UserStore.actionCreators, dispatch);

export default withStyles(styles)(connect(reduxState, reduxDispatch)(NavMenu));
